import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { PencilIcon, UserPlusIcon } from '@heroicons/react/24/solid';
import { Button, Link } from '@heroui/react';
import _ from 'lodash';
import { type FC, useCallback, useState } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';
import { Tooltip } from 'react-tooltip';
import AccountDeactivatedBadge from '@/app/components/account/account-deactivated-badge.tsx';
import AccountFlagBadge from '@/app/components/account/account-flag-badge.tsx';
import ConnectRepDialog from '@/app/components/account/connect-rep-dialog.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import {
  ResponsiveTable,
  TableTile,
  TableTileField,
  TableTileList,
} from '@/primitives/responsive-table.tsx';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import type { AccountListModel } from '../../types/account.type.ts';
import { hasPermission } from '../../types/constants.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import { NoData } from '../no-data.tsx';
import { Paginator, usePagination } from '../paginator.tsx';

export interface AccountsListProps {
  accounts: AccountListModel[];
}

interface AccountActionsProps {
  account: AccountListModel;
  onConnectRep: (account: AccountListModel) => void;
}

const AccountActions: FC<AccountActionsProps> = ({ account, onConnectRep }) => {
  const { user } = useAuth();
  const isDeactivated = !!account.deactivatedAt;

  const canManage =
    user?.userType === UserTypeEnum.Admin ||
    (user?.userType === UserTypeEnum.StaffMember &&
      hasPermission(user as StaffMemberModel, StaffRoleEnum.SiteAdministrator, account.id));

  if (!canManage) {
    return null;
  }

  const isAdmin = user?.userType === UserTypeEnum.Admin;

  return (
    <div className='flex flex-wrap items-center justify-end gap-2'>
      {!isDeactivated && (
        <>
          <Button
            size='sm'
            variant='bordered'
            color='primary'
            as={Link}
            href={`/account/edit/${account.key}`}
          >
            <PencilIcon className='size-4' title='Edit Details' />
            Edit
          </Button>
          <Button
            size='sm'
            variant='bordered'
            color='primary'
            as={Link}
            href={`/account/settings/${account.key}`}
          >
            <Cog6ToothIcon className='size-4' title='Account settings' />
            Settings
          </Button>
        </>
      )}
      {isAdmin && (
        <Button
          size='sm'
          variant='bordered'
          color='secondary'
          startContent={<UserPlusIcon className='size-4' />}
          onPress={() => onConnectRep(account)}
        >
          Connect Rep
        </Button>
      )}
    </div>
  );
};

const AccountName: FC<{ account: AccountListModel }> = ({ account }) => {
  const isDeactivated = !!account.deactivatedAt;

  return (
    <div className='flex flex-wrap items-center gap-2'>
      {isDeactivated ? (
        <span className='font-medium text-zinc-900 dark:text-white'>{account.accountName}</span>
      ) : (
        <TextLink href={`/account/info/${account.key}`}>{account.accountName}</TextLink>
      )}
      <AccountFlagBadge accountFlagId={account.accountFlagId} />
      <AccountDeactivatedBadge deactivatedAt={account.deactivatedAt} />
    </div>
  );
};

const AccountsList: FC<AccountsListProps> = ({ accounts }) => {
  const memoizedSortingDataAccessor = useCallback((value: AccountListModel, column: string) => {
    switch (column) {
      case 'city':
        return value.address.city;
      case 'specialties':
        return value.specialties.join(', ');
      default:
        return _.get(value, column);
    }
  }, []);

  const { sort, setSort, sortData } = useSort({
    data: accounts,
    memoizedSortingDataAccessor,
  });

  const pager = usePagination({ data: sortData, resourceName: 'accounts' });
  const { pagedData } = pager;

  const [connectDialogAccount, setConnectDialogAccount] = useState<AccountListModel | null>(null);
  const hasAccounts = Boolean(pagedData?.length);

  return (
    <>
      <ResponsiveTable
        currentSort={sort}
        setCurrentSort={setSort}
        tiles={
          <TableTileList empty={!hasAccounts} emptyContent={<NoData />}>
            {hasAccounts &&
              pagedData.map(value => (
                <TableTile key={value.id}>
                  <div className='flex flex-col gap-3'>
                    <TableTileField label='Name'>
                      <AccountName account={value} />
                    </TableTileField>
                    <TableTileField label='Type'>{value.accountType}</TableTileField>
                    <TableTileField label='Location'>
                      {value.address.city}, {value.address.state.abbreviation}
                    </TableTileField>
                    <TableTileField label='Specialties'>
                      {value.specialties.length ? value.specialties.join(', ') : '--'}
                    </TableTileField>
                    <TableTileField label='Email'>
                      <TextLink href={`mailto:${value.emailAddress}`}>
                        {value.emailAddress}
                      </TextLink>
                    </TableTileField>
                    <TableTileField label='Phone'>
                      {formatPhoneNumber(value.phoneNumber)}
                    </TableTileField>
                    <TableTileField label='Actions'>
                      <AccountActions account={value} onConnectRep={setConnectDialogAccount} />
                    </TableTileField>
                  </div>
                </TableTile>
              ))}
          </TableTileList>
        }
      >
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='accountName'>Name</TableHeader>
            <TableHeader sortColumn='accountType'>Type</TableHeader>
            <TableHeader sortColumn='city'>Location</TableHeader>
            <TableHeader sortColumn='specialties'>Specialties</TableHeader>
            <TableHeader sortColumn='emailAddress'>Email</TableHeader>
            <TableHeader sortColumn='phoneNumber'>Phone</TableHeader>
            <TableHeader className='text-right'>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {!hasAccounts && (
            <TableRow>
              <TableCell colSpan={7}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
          {hasAccounts &&
            pagedData.map(value => (
              <TableRow key={value.id}>
                <TableCell>
                  <AccountName account={value} />
                </TableCell>
                <TableCell>{value.accountType}</TableCell>
                <TableCell>
                  {value.address.city}, {value.address.state.abbreviation}
                </TableCell>
                <TableCell id={`specialty-tooltip-${value.id}`}>
                  {_.truncate(value.specialties.join(', '), { length: 30, separator: ',' })}
                  <Tooltip
                    anchorSelect={`#specialty-tooltip-${value.id}`}
                    content={value.specialties.join(', ')}
                  />
                </TableCell>
                <TableCell>
                  <TextLink href={`mailto:${value.emailAddress}`}>{value.emailAddress}</TextLink>
                </TableCell>
                <TableCell>{formatPhoneNumber(value.phoneNumber)}</TableCell>
                <TableCell className='text-right'>
                  <AccountActions account={value} onConnectRep={setConnectDialogAccount} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </ResponsiveTable>
      <Paginator {...pager} />
      {connectDialogAccount && (
        <ConnectRepDialog
          accountKey={connectDialogAccount.key}
          accountName={connectDialogAccount.accountName}
          isOpen={!!connectDialogAccount}
          onClose={() => setConnectDialogAccount(null)}
        />
      )}
    </>
  );
};

export default AccountsList;
